/**
 * ClaimantCard — Draggable Kanban card displayed in PipelineBoard columns.
 * Blueprint: Section D — ClaimantCard modifications
 *
 * TODO: Add drag handle for react-beautiful-dnd or @dnd-kit/core.
 * TODO: Show deficiency badge (red) when has_deficiency=true.
 * TODO: Show overlap badge (amber) when has_overlap=true.
 * TODO: Show cross-litigation warning badge when cross_litigation_flag=true (F-08).
 * TODO: Click → opens ClaimantDetailPanel slide-over.
 */
import React from 'react';
import { AlertTriangle, GitMerge, FileWarning, User } from 'lucide-react';
import type { Claimant, TortCode } from '@/types/cmm/claimant.types';

interface ClaimantCardProps {
  claimant:  Claimant;
  onClick?:  (claimant: Claimant) => void;
  isDragging?: boolean;
}

const TORT_COLORS: Record<TortCode, string> = {
  SMA:  'bg-purple-100 text-purple-700 border-purple-200',
  VGA:  'bg-blue-100   text-blue-700   border-blue-200',
  RLP:  'bg-green-100  text-green-700  border-green-200',
  PFAS: 'bg-orange-100 text-orange-700 border-orange-200',
};

export const ClaimantCard: React.FC<ClaimantCardProps> = ({
  claimant, onClick, isDragging = false,
}) => {
  const fullName = `${claimant.firstName} ${claimant.lastName}`;

  return (
    <div
      className={`
        bg-white rounded-lg border border-gray-200 p-3 cursor-pointer
        shadow-sm hover:shadow-md transition-shadow select-none
        ${isDragging ? 'opacity-75 rotate-1 shadow-lg' : ''}
      `}
      onClick={() => onClick?.(claimant)}
      role="button"
      tabIndex={0}
      aria-label={`Claimant: ${fullName}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
            <User className="w-4 h-4 text-gray-500" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900 leading-tight">{fullName}</p>
            <p className="text-xs text-gray-500">{claimant.state} · DOB {claimant.dob}</p>
          </div>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${TORT_COLORS[claimant.tortCode]}`}>
          {claimant.tortCode}
        </span>
      </div>

      {/* Status Badges */}
      <div className="flex gap-1 flex-wrap">
        {claimant.crossLitigationFlag && (
          <span className="inline-flex items-center gap-1 text-xs bg-yellow-50 text-yellow-700 border border-yellow-200 px-1.5 py-0.5 rounded">
            <AlertTriangle className="w-3 h-3" />
            Cross-Tort
          </span>
        )}
        {(claimant.overlapPriorityTier ?? 0) > 0 && (
          <span className="inline-flex items-center gap-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">
            <GitMerge className="w-3 h-3" />
            Overlap T{claimant.overlapPriorityTier}
          </span>
        )}
      </div>

      {/* Footer */}
      <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
        <span className="text-xs text-gray-400">#{claimant.id.slice(0, 8)}</span>
        {claimant.estPortfolioValueRange && (
          <span className="text-xs font-medium text-gray-600">{claimant.estPortfolioValueRange}</span>
        )}
      </div>
    </div>
  );
};

export default ClaimantCard;
